import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SidebarCollapsed } from '@core/constants/app-constant';
import { environment } from '@env';
import { AuthorizationFacade } from '@features/authorization/facades/authorization.facade';
import { TranslocoDirective } from '@jsverse/transloco';
import type { NamedRole } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { IconComponent, NavigationRegistryService } from '@kerberos-compliance/compliance-shared-ui';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuDirective, NzMenuItemComponent, NzMenuModule, NzSubMenuComponent } from 'ng-zorro-antd/menu';
import type { NavigationMenu } from './types/navigation-menu.model';

@Component({
  selector: 'aad-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrl: './navigation-menu.component.less',
  imports: [
    RouterLink,
    NzMenuModule,
    NzSubMenuComponent,
    NzMenuDirective,
    NzMenuItemComponent,
    NzIconModule,
    TranslocoDirective,
    IconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NavigationMenuComponent {
  public isCollapsed = input<boolean>(SidebarCollapsed);

  private readonly navRegistry = inject(NavigationRegistryService);

  private readonly authFacade = inject(AuthorizationFacade);

  protected readonly navigationItems = computed((): NavigationMenu[] => {
    const canAccess = (roles?: NamedRole[]): boolean =>
      environment.e2eMode || !environment.enableMsal || !roles?.length || roles.some((r) => this.authFacade.hasRole(r));

    const items = this.navRegistry.navItems().filter((entry) => canAccess(entry.roles));
    const topLevel = items.filter(
      (routeEntry) => routeEntry.nav && !items.some((p) => p.key === routeEntry.parentKey && p.nav),
    );

    return topLevel.map(
      (parent): NavigationMenu => ({
        name: parent.nav!.labelKey,
        translationKey: parent.nav!.labelKey,
        icon: parent.nav!.icon,
        route: ['/', ...parent.segments],
        open: true,
        children: items
          .filter((child) => child.parentKey === parent.key && child.nav)
          .map(
            (child): NavigationMenu => ({
              name: child.nav!.labelKey,
              translationKey: child.nav!.labelKey,
              icon: child.nav!.icon,
              route: ['/', ...child.segments],
            }),
          ),
      }),
    );
  });
}
